
import { useCallback } from "react";
import { Layout } from "components";
import { SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { Typography } from "theme";

import { zodResolver } from "@hookform/resolvers/zod";
import { Box } from "@mui/material";
import { styled } from "@mui/system";
import { useMutation } from "@tanstack/react-query";

import { Urls } from "../../api/urls.ts";
import ControlledTextField from "../../components/ControlledInputs/ControlledTextField.tsx";
import { InvertButton } from "../../theme/components/Button/Button.tsx";
import { useAuth } from "../../utils/context/Auth/AuthContext.tsx";
import useFetch from "../../utils/hooks/useFetch.ts";

import { spendingSchema, SpendingSchemaFormScheme } from "./validation.ts";

const FormWrapper = styled(Box)(() => ({
  display: "flex",
  justifyContent: 'center',
  alignItems: 'center',
}));

const StyledForm = styled(Box)(() => ({
  maxWidth: '454px',
  width: '100%',
}));


const Spending = () => {
  const request = useFetch();
  const { token } = useAuth();

  const { mutate: spending, isPending } = useMutation({
    mutationFn: ({ amount, description }: SpendingSchemaFormScheme) => {
      return request<any>(`${Urls.Outcome}?amount=${amount}&description=${description}`, {
        method: "POST",
      }, true, token);
    },
    onError: (error) => {
      toast.error(error.message);
    },
    onSuccess: () => {
      toast.success('Витрати успішно додані');
    },
  });

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors, isDirty },
  } = useForm<SpendingSchemaFormScheme>({
    reValidateMode: "onSubmit",
    resolver: zodResolver(spendingSchema),
    defaultValues: {
      amount: '0',
      description: ""
    },
  });

  const onSubmit:SubmitHandler<SpendingSchemaFormScheme> = useCallback((data) => {
    if (!isDirty) return;

    spending(data);
    reset();
  }, [isDirty, reset, spending])

  return (
    <Layout>
      <FormWrapper>
        <StyledForm>
          <Typography variant='h1' textTransform='uppercase' textAlign='center'>Витрати</Typography>
          <Typography mt={2} variant='body1' textTransform='uppercase' textAlign='center'>Форма для додавання витрат Panorama Studio</Typography>
          <Box
            mt={3.5}
            component="form"
            id="sign-in-form"
            noValidate
            onSubmit={handleSubmit(onSubmit)}
          >
            <Box>
              <ControlledTextField
                control={control}
                required
                name="amount"
                label="Сума витрат"
                type="number"
                autoComplete="off"
              />
              {errors.amount?.message && <Typography color='error' variant='body2'>{errors.amount.message}</Typography>}
            </Box>
            <Box mt={2}>
              <ControlledTextField
                control={control}
                required
                name="description"
                label="Тип витрат"
                type="text"
              />
              {errors.description?.message && <Typography color='error' variant='body2'>{errors.description.message}</Typography>}
            </Box>
            <Box mt={4}>
              <InvertButton
                type="submit"
                form='sign-in-form'
                loading={isPending}
                size="large"
                fullWidth
              >
                Додати витрату
              </InvertButton>
            </Box>
          </Box>
        </StyledForm>
      </FormWrapper>
    </Layout>
  );
};

export default Spending;